import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';

// Sync Convenia — estado do job que mantém o espelho de férias igual à Convenia.
// A Convenia não avisa quando uma solicitação é excluída nem quando nasce um período
// aquisitivo, então esse job é a única garantia de que o VA/VT é calculado com o dado
// certo. Por isso a página abre pelo estado (verde/vermelho), não pela tabela: se ele
// parar de rodar, tem de saltar aos olhos.

const STATUS = {
  ok:      { bg: '#e6f7ef', cor: '#116b47', borda: '#a8dcc3', rotulo: 'ok',      icone: 'fa-circle-check' },
  alerta:  { bg: '#fff5e6', cor: '#8a5a00', borda: '#f2d9a8', rotulo: 'alerta',  icone: 'fa-triangle-exclamation' },
  erro:    { bg: '#fdeaec', cor: '#a11d2b', borda: '#f3b8bf', rotulo: 'erro',    icone: 'fa-circle-exclamation' },
  rodando: { bg: '#e8f1fb', cor: '#0a4f8a', borda: '#b7d3ee', rotulo: 'rodando', icone: 'fa-spinner' },
  parcial: { bg: '#fff5e6', cor: '#8a5a00', borda: '#f2d9a8', rotulo: 'parcial', icone: 'fa-triangle-exclamation' },
};
const ACOES = {
  inserida:   { bg: '#e6f7ef', cor: '#116b47', borda: '#a8dcc3', icone: 'fa-circle-plus' },
  atualizada: { bg: '#e8f1fb', cor: '#0a4f8a', borda: '#b7d3ee', icone: 'fa-pen-to-square' },
  removida:   { bg: '#fdeaec', cor: '#a11d2b', borda: '#f3b8bf', icone: 'fa-circle-minus' },
};

const Badge = ({ mapa, chave, texto }) => {
  const e = mapa[chave] || { bg: '#eef2f7', cor: '#495057', borda: '#d6dee8', icone: 'fa-circle' };
  return (
    <span className="badge" style={{
      background: e.bg, color: e.cor, border: `1px solid ${e.borda}`, fontWeight: 600,
    }}>
      <i className={`fas ${e.icone} me-1`}></i>{texto || e.rotulo || chave || '—'}
    </span>
  );
};

const ABAS = [
  { id: 'execucoes', rotulo: 'Execuções', icone: 'fa-clock-rotate-left' },
  { id: 'mudancas', rotulo: 'O que foi atualizado', icone: 'fa-list-check' },
  { id: 'clientes', rotulo: 'Por empresa', icone: 'fa-building' },
  { id: 'alertas', rotulo: 'Alertas enviados', icone: 'fa-bell' },
];

const SyncConvenia = () => {
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [dias, setDias] = useState('7');
  const [aba, setAba] = useState('execucoes');
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/bporh/sync-convenia/`, { params: { dias }, headers: headers() })
      .then((res) => setDados(res.data))
      .catch(() => { setDados(null); setMensagem('Não foi possível carregar o painel'); })
      .finally(() => setCarregando(false));
  }, [dias]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [carregar]);

  const abrirRodada = (execucao) => {
    setDetalhe({ carregando: true });
    axios.get(`${API}/api/bporh/sync-convenia/${execucao}/`, { headers: headers() })
      .then((res) => setDetalhe(res.data.detalhe))
      .catch(() => { setDetalhe(null); setMensagem('Não foi possível abrir a rodada'); });
  };

  const u = dados?.ultima;
  const avisos = dados?.avisos || [];
  const acao = dados?.por_acao || {};
  const estado = dados?.estado || (dados?.saudavel ? 'ok' : 'problema');
  const ESTADO = {
    ok: { fundo: '#e6f7ef', cor: '#116b47', icone: 'fa-circle-check', titulo: 'Sincronização em dia' },
    rodando: { fundo: '#e8f1fb', cor: '#0a4f8a', icone: 'fa-spinner fa-spin', titulo: 'Sincronização em andamento' },
    problema: { fundo: '#fdeaec', cor: '#a11d2b', icone: 'fa-circle-exclamation',
      titulo: 'Sincronização com problema — as férias podem estar desatualizadas' },
  }[estado];

  return (
    <div className="container-fluid px-4 py-3">
      {/* ─── estado: é a primeira coisa que a página responde ─── */}
      {!carregando && (
        <div className="card border-0 shadow-sm mb-3" style={{
          background: ESTADO.fundo, borderLeft: `6px solid ${ESTADO.cor}`,
        }}>
          <div className="card-body">
            <div className="d-flex align-items-start gap-3">
              <i className={`fas ${ESTADO.icone} fs-3`} style={{ color: ESTADO.cor }}></i>
              <div className="flex-grow-1">
                <h5 className="fw-bold mb-1" style={{ color: ESTADO.cor }}>{ESTADO.titulo}</h5>
                {u ? (
                  <div className="small" style={{ color: '#495057' }}>
                    Última execução em <b>{u.finalizado_em || u.iniciado_em}</b>
                    {u.ha_horas != null && ` (há ${u.ha_horas} h)`}
                    {u.duracao_min != null && ` · durou ${u.duracao_min} min`}
                    {' · '}{u.resumo}
                    {dados?.proxima_prevista && (
                      <> · próxima prevista para <b>{dados.proxima_prevista}</b></>
                    )}
                  </div>
                ) : (
                  <div className="small" style={{ color: '#495057' }}>
                    Nenhuma execução registrada até agora.
                  </div>
                )}
                {avisos.map((a, i) => (
                  <div key={i} className="mt-2 p-2 rounded" style={{ background: '#fff' }}>
                    <div className="fw-bold small" style={{ color: '#a11d2b' }}>
                      <i className="fas fa-triangle-exclamation me-2"></i>{a.texto}
                    </div>
                    <div className="small text-muted mt-1">
                      {a.email_enviado
                        ? <><i className="fas fa-envelope-circle-check me-1" style={{ color: '#116b47' }}></i>
                          E-mail de alerta enviado para {(dados?.destinatarios_alerta || []).join(', ')}</>
                        : <><i className="fas fa-envelope-open-text me-1" style={{ color: '#a11d2b' }}></i>
                          <b>O e-mail de alerta NÃO saiu</b>{a.email_erro ? `: ${a.email_erro}` : ''}</>}
                    </div>
                  </div>
                ))}
                {u?.problemas && (
                  <pre className="small mt-2 mb-0 p-2 rounded" style={{
                    background: '#fff', whiteSpace: 'pre-wrap', color: '#a11d2b',
                  }}>{u.problemas}</pre>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── números da última rodada e do período ─── */}
      <div className="row g-2 mb-3">
        {[
          { rot: 'Colaboradores lidos', val: u?.colab_lidos ?? '—', ico: 'fa-users', cor: '#00205b' },
          { rot: 'Mudanças na última rodada', val: u?.mudancas ?? '—', ico: 'fa-pen-to-square', cor: '#0a4f8a' },
          { rot: 'Inseridas no período', val: acao.inserida ?? 0, ico: 'fa-circle-plus', cor: '#116b47' },
          { rot: 'Atualizadas no período', val: acao.atualizada ?? 0, ico: 'fa-pen-to-square', cor: '#0a4f8a' },
          { rot: 'Removidas no período', val: acao.removida ?? 0, ico: 'fa-circle-minus', cor: '#a11d2b' },
        ].map((c) => (
          <div className="col-6 col-md" key={c.rot}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body py-2">
                <div className="small text-muted fw-bold">
                  <i className={`fas ${c.ico} me-2`}></i>{c.rot}
                </div>
                <div className="fs-4 fw-bold" style={{ color: c.cor }}>{c.val}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── abas + controles ─── */}
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <div className="btn-group">
          {ABAS.map((a) => (
            <button key={a.id}
              className={`btn btn-sm ${aba === a.id ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAba(a.id)}>
              <i className={`fas ${a.icone} me-2`}></i>{a.rotulo}
            </button>
          ))}
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <span className="text-muted small d-none d-lg-inline">
            Atualiza a cada {dados?.intervalo_horas || 2} h
          </span>
          <select className="form-select form-select-sm" style={{ width: '120px' }}
            value={dias} onChange={(e) => setDias(e.target.value)}>
            <option value="1">Hoje</option>
            <option value="7">7 dias</option>
            <option value="30">30 dias</option>
            <option value="90">90 dias</option>
          </select>
          <button className="btn btn-outline-primary btn-sm" onClick={carregar} disabled={carregando}>
            <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
            Atualizar
          </button>
        </div>
      </div>

      <div className="table-responsive bg-white rounded shadow-sm">
        {aba === 'execucoes' && (
          <table className="table table-hover align-middle mb-0">
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th><i className="fas fa-clock me-2"></i>Início</th>
                <th>Fim</th>
                <th>Status</th>
                <th className="text-end">Duração</th>
                <th className="text-end">Colaboradores</th>
                <th className="text-end">Mudanças</th>
                <th>Resumo</th>
                <th className="text-center">Ver</th>
              </tr>
            </thead>
            <tbody>
              {(dados?.execucoes || []).map((e) => {
                const ruim = e.status !== 'ok';
                return (
                  <tr key={e.execucao} style={{ cursor: 'pointer', background: ruim ? '#fff6f7' : undefined }}
                    onClick={() => abrirRodada(e.execucao)}>
                    <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {ruim && <i className="fas fa-triangle-exclamation me-2" style={{ color: '#a11d2b' }}></i>}
                      {e.iniciado_em || '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{e.finalizado_em || '—'}</td>
                    <td><Badge mapa={STATUS} chave={e.status} /></td>
                    <td className="text-end">{e.duracao_min != null ? `${e.duracao_min} min` : '—'}</td>
                    <td className="text-end">{e.colab_lidos ?? '—'}</td>
                    <td className="text-end fw-bold">{e.mudancas ?? 0}</td>
                    <td className="small text-muted" style={{ maxWidth: '420px' }}>
                      {e.problemas
                        ? <span style={{ color: '#a11d2b' }}>{e.problemas.split('\n')[0]}</span>
                        : (e.resumo || '—')}
                      {ruim && !e.alerta_enviado && (
                        <div className="fw-bold" style={{ color: '#a11d2b' }}>
                          <i className="fas fa-envelope-open-text me-1"></i>
                          e-mail de alerta não saiu{e.alerta_erro ? `: ${e.alerta_erro}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-primary py-0 px-2"
                        onClick={(ev) => { ev.stopPropagation(); abrirRodada(e.execucao); }}>
                        <i className="fas fa-eye"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!carregando && !(dados?.execucoes || []).length && (
                <tr><td colSpan={8} className="text-center text-muted py-4">
                  <i className="fas fa-inbox me-2"></i>Nenhuma execução no período.
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        {aba === 'mudancas' && (
          <table className="table table-striped table-hover align-middle mb-0">
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th><i className="fas fa-clock me-2"></i>Quando</th>
                <th>Empresa</th>
                <th>Colaborador</th>
                <th>Registro</th>
                <th>Ação</th>
                <th>O que mudou</th>
              </tr>
            </thead>
            <tbody>
              {(dados?.mudancas || []).map((m, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{m.quando}</td>
                  <td>{m.cliente}</td>
                  <td>{m.colaborador}</td>
                  <td className="small">{m.tipo}</td>
                  <td><Badge mapa={ACOES} chave={m.acao} texto={m.acao} /></td>
                  <td className="small text-muted" style={{ maxWidth: '520px' }}>{m.resumo}</td>
                </tr>
              ))}
              {!carregando && !(dados?.mudancas || []).length && (
                <tr><td colSpan={6} className="text-center text-muted py-4">
                  <i className="fas fa-inbox me-2"></i>
                  Nenhuma mudança no período — o espelho já estava igual à Convenia.
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        {aba === 'clientes' && (
          <table className="table table-striped table-hover align-middle mb-0">
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th><i className="fas fa-building me-2"></i>Empresa</th>
                <th>Última sincronização</th>
                <th>Status</th>
                <th className="text-end">Mudanças</th>
                <th>Resumo</th>
              </tr>
            </thead>
            <tbody>
              {(dados?.clientes || []).map((c) => (
                <tr key={c.id} style={{ background: c.status && c.status !== 'ok' ? '#fff6f7' : undefined }}>
                  <td>
                    {c.cliente}
                    {!c.com_token && (
                      <span className="badge ms-2" style={{
                        background: '#eef2f7', color: '#495057', border: '1px solid #d6dee8',
                      }}>sem token</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.quando || '—'}
                    {c.ha_horas != null && <span className="text-muted small ms-2">há {c.ha_horas} h</span>}
                  </td>
                  <td>{c.status ? <Badge mapa={STATUS} chave={c.status} /> : <span className="text-muted">—</span>}</td>
                  <td className="text-end">{c.mudancas ?? '—'}</td>
                  <td className="small text-muted" style={{ maxWidth: '520px' }}>{c.resumo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {aba === 'alertas' && (
          <table className="table table-striped align-middle mb-0">
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th><i className="fas fa-clock me-2"></i>Quando</th>
                <th>Motivo</th>
                <th>Texto</th>
                <th>E-mail</th>
                <th>Para</th>
              </tr>
            </thead>
            <tbody>
              {(dados?.alertas || []).map((a, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: 'nowrap' }}>{a.quando}</td>
                  <td><code>{a.motivo}</code></td>
                  <td className="small">{a.texto}</td>
                  <td>
                    {a.enviado
                      ? <span className="badge" style={{ background: '#e6f7ef', color: '#116b47', border: '1px solid #a8dcc3' }}>
                        <i className="fas fa-envelope-circle-check me-1"></i>enviado</span>
                      : <span className="badge" style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #f3b8bf' }}>
                        <i className="fas fa-envelope-open-text me-1"></i>falhou</span>}
                    {a.erro && <div className="small" style={{ color: '#a11d2b' }}>{a.erro}</div>}
                  </td>
                  <td className="small text-muted">{a.destinatarios}</td>
                </tr>
              ))}
              {!carregando && !(dados?.alertas || []).length && (
                <tr><td colSpan={5} className="text-center text-muted py-4">
                  <i className="fas fa-inbox me-2"></i>Nenhum alerta disparado até agora.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── MODAL: detalhe da rodada ─── */}
      {detalhe && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-rotate me-2"></i>Rodada {detalhe.execucao || ''}
                  </h5>
                  <small className="text-muted">
                    {detalhe.iniciado_em} {detalhe.finalizado_em && `até ${detalhe.finalizado_em}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
              </div>
              <div className="modal-body">
                {detalhe.carregando ? (
                  <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : (
                  <>
                    <div className="row g-2 mb-3 small">
                      <div className="col-6"><b>Status:</b> <Badge mapa={STATUS} chave={detalhe.status} /></div>
                      <div className="col-6"><b>Alerta por e-mail:</b>{' '}
                        {detalhe.status === 'ok'
                          ? <span className="text-muted">não precisou</span>
                          : detalhe.alerta_enviado
                            ? <span style={{ color: '#116b47' }}>enviado</span>
                            : <span style={{ color: '#a11d2b' }}>falhou{detalhe.alerta_erro ? `: ${detalhe.alerta_erro}` : ''}</span>}
                      </div>
                      <div className="col-12"><b>Resumo:</b> {detalhe.resumo || '—'}</div>
                    </div>
                    {detalhe.problemas && (
                      <>
                        <div className="small fw-bold mb-1" style={{ color: '#a11d2b' }}>Problemas</div>
                        <pre className="border rounded p-3 small" style={{
                          background: '#fdeaec', whiteSpace: 'pre-wrap', color: '#a11d2b',
                        }}>{detalhe.problemas}</pre>
                      </>
                    )}
                    <div className="small fw-bold text-muted mb-1">Cliente a cliente</div>
                    <table className="table table-sm table-bordered small mb-0">
                      <thead style={{ background: '#f8f9fa' }}>
                        <tr>
                          <th>Empresa</th><th>Status</th><th className="text-end">Lidos</th>
                          <th className="text-end">Solicitações<br />+ / ~ / −</th>
                          <th className="text-end">Períodos<br />+ / ~ / −</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detalhe.clientes || []).map((c, i) => (
                          <tr key={i}>
                            <td>{c.cliente}</td>
                            <td><Badge mapa={STATUS} chave={c.status} /></td>
                            <td className="text-end">{c.colab_lidos ?? '—'}</td>
                            <td className="text-end">
                              {c.solic_inseridas ?? 0} / {c.solic_atualizadas ?? 0} / {c.solic_removidas ?? 0}
                            </td>
                            <td className="text-end">
                              {c.periodos_inseridos ?? 0} / {c.periodos_atualizados ?? 0} / {c.periodos_removidos ?? 0}
                            </td>
                          </tr>
                        ))}
                        {!(detalhe.clientes || []).length && (
                          <tr><td colSpan={5} className="text-center text-muted py-3">
                            Sem detalhe por cliente nesta rodada.
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncConvenia;
